import React, { useState } from 'react';
import { Link } from 'react-router-dom';

// SVG Icons
const Search = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/>
    <path d="m21 21-4.35-4.35"/>
  </svg>
);

const TrendingUp = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
    <polyline points="17 6 23 6 23 12"/>
  </svg>
);

const Star = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
  </svg>
);

const Loader2 = ({ size = 24, ...props }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
  </svg>
);

const BarChart3 = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 3v18h18"/>
    <path d="M18 17V9"/>
    <path d="M13 17V5"/>
    <path d="M8 17v-3"/>
  </svg>
);

export default function ReviewAggregator() {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [demoMode] = useState(true); // Set to false when you have a backend

  // Mock data for demo purposes
  const getMockData = (query) => {
    const products = {
      'iphone': {
        overallRating: 8.5,
        confidence: 'high',
        totalReviews: 847,
        summary: 'The iPhone 15 Pro receives praise for its powerful A17 Pro chip, impressive camera system, and new titanium design. However, some users note the high price point and incremental upgrades over previous models.',
        pros: [
          'Exceptional camera quality with improved low-light performance',
          'Powerful A17 Pro chip delivers outstanding performance',
          'Premium titanium build feels durable and lightweight'
        ],
        cons: [
          'Premium pricing makes it less accessible',
          'Battery life could be better for heavy users',
          'Minor heating issues reported during intensive tasks'
        ],
        breakdown: {
          quality: 9,
          value: 7,
          performance: 9
        },
        sourceRatings: [
          { platform: 'Amazon', rating: '4.6/5', reviewCount: 1243 },
          { platform: 'Best Buy', rating: '4.7/5', reviewCount: 892 },
          { platform: 'Apple Store', rating: '4.8/5', reviewCount: 3456 },
          { platform: 'CNET', rating: '8.5/10', reviewCount: 15 },
          { platform: 'Tom\'s Guide', rating: '4.5/5', reviewCount: 8 }
        ]
      },
      'airpods': {
        overallRating: 7.8,
        confidence: 'high',
        totalReviews: 523,
        summary: 'AirPods Pro receive strong marks for their active noise cancellation and seamless Apple ecosystem integration. The sound quality is good, though audiophiles may find better options at this price point.',
        pros: [
          'Excellent active noise cancellation',
          'Seamless integration with Apple devices',
          'Comfortable fit for extended wear'
        ],
        cons: [
          'Premium price compared to competitors',
          'Sound quality good but not exceptional',
          'Limited customization options'
        ],
        breakdown: {
          quality: 8,
          value: 7,
          performance: 8
        },
        sourceRatings: [
          { platform: 'Amazon', rating: '4.5/5', reviewCount: 8934 },
          { platform: 'Best Buy', rating: '4.6/5', reviewCount: 2341 },
          { platform: 'Apple Store', rating: '4.7/5', reviewCount: 5678 },
          { platform: 'RTINGS', rating: '7.8/10', reviewCount: 12 }
        ]
      },
      'default': {
        overallRating: 7.2,
        confidence: 'medium',
        totalReviews: 156,
        summary: `Reviews for ${query} show mixed reactions. Users appreciate certain features but note room for improvement in several areas. The product delivers decent value but faces strong competition.`,
        pros: [
          'Good build quality and design',
          'Reasonable pricing for features offered',
          'Positive user experience in most cases'
        ],
        cons: [
          'Some quality control issues reported',
          'Customer service could be more responsive',
          'Missing features available in competitor products'
        ],
        breakdown: {
          quality: 7,
          value: 8,
          performance: 7
        },
        sourceRatings: [
          { platform: 'Google Reviews', rating: '4.2/5', reviewCount: 234 },
          { platform: 'Yelp', rating: '3.8/5', reviewCount: 156 },
          { platform: 'Trustpilot', rating: '4.0/5', reviewCount: 89 }
        ]
      }
    };

    const queryLower = query.toLowerCase();
    if (queryLower.includes('iphone') || queryLower.includes('15')) {
      return products.iphone;
    } else if (queryLower.includes('airpod')) {
      return products.airpods;
    } else {
      return products.default;
    }
  };

  const analyzeReviews = async () => {
    if (!query.trim()) return;
    
    setLoading(true);
    setResult(null);

    try {
      if (demoMode) {
        // Demo mode - simulate API delay
        await new Promise(resolve => setTimeout(resolve, 2000));
        const mockData = getMockData(query);
        setResult(mockData);
      } else {
        // Real API mode - calls your Heroku backend
        const response = await fetch('https://your-heroku-app.herokuapp.com/api/analyze-reviews', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ query })
        });

        if (!response.ok) {
          throw new Error('Failed to analyze reviews');
        }

        const data = await response.json();
        setResult(data);
      }
    } catch (error) {
      console.error('Error analyzing reviews:', error);
      setResult({
        error: true,
        message: 'Unable to analyze reviews. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    analyzeReviews();
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 50%, #60a5fa 100%)',
      fontFamily: '"IBM Plex Sans", system-ui, -apple-system, sans-serif',
      color: '#ffffff',
      display: 'flex',
      flexDirection: 'column',
      paddingTop: '70px',
      paddingBottom: '70px'
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=Space+Mono:wght@400;700&display=swap');
        
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
        
        @keyframes shimmer {
          0% {
            background-position: -1000px 0;
          }
          100% {
            background-position: 1000px 0;
          }
        }
        
        .animate-up {
          animation: slideUp 0.6s ease-out forwards;
        }
        
        .rating-circle {
          position: relative;
          width: 200px;
          height: 200px;
          border-radius: 50%;
          background: linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 20px 60px rgba(30, 58, 138, 0.6);
        }
        
        .rating-circle::before {
          content: '';
          position: absolute;
          inset: 6px;
          border-radius: 50%;
          background: #1a1a2e;
        }
        
        .rating-value {
          position: relative;
          font-family: 'Space Mono', monospace;
          font-weight: 700;
          display: flex;
          flex-direction: column;
          align-items: center;
          line-height: 1;
          color: #ffffff;
          z-index: 1;
        }
        
        .rating-number {
          font-size: 4rem;
          margin-bottom: 0.25rem;
        }
        
        .rating-label {
          font-size: 1.25rem;
          font-weight: 600;
          letter-spacing: 0.1em;
          opacity: 0.9;
        }
        
        .stat-card {
          background: rgba(255, 255, 255, 0.15);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.25);
          border-radius: 16px;
          padding: 1.5rem;
          transition: all 0.3s ease;
        }
        
        .stat-card:hover {
          background: rgba(255, 255, 255, 0.2);
          transform: translateY(-4px);
          box-shadow: 0 12px 24px rgba(0, 0, 0, 0.3);
        }
        
        .search-input {
          background: rgba(255, 255, 255, 0.1);
          border: 2px solid rgba(255, 255, 255, 0.2);
          border-radius: 16px;
          padding: 1.25rem 1.5rem;
          font-size: 1.125rem;
          color: #ffffff;
          width: 100%;
          outline: none;
          transition: all 0.3s ease;
          font-family: 'IBM Plex Sans', sans-serif;
        }
        
        .search-input::placeholder {
          color: rgba(255, 255, 255, 0.5);
        }
        
        .search-input:focus {
          background: rgba(255, 255, 255, 0.15);
          border-color: #3b82f6;
          box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.2);
        }
        
        .search-button {
          background: linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%);
          border: none;
          border-radius: 16px;
          padding: 1.25rem 2.5rem;
          font-size: 1.125rem;
          font-weight: 600;
          color: #ffffff;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 8px 20px rgba(59, 130, 246, 0.4);
        }
        
        .search-button:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 12px 28px rgba(59, 130, 246, 0.5);
        }
        
        .search-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        
        .pill {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 100px;
          font-size: 0.875rem;
          font-weight: 500;
        }
        
        .progress-bar {
          height: 8px;
          background: rgba(255, 255, 255, 0.25);
          border-radius: 100px;
          overflow: hidden;
          position: relative;
        }
        
        .progress-fill {
          height: 100%;
          background: linear-gradient(90deg, #fbbf24 0%, #fcd34d 100%);
          border-radius: 100px;
          transition: width 0.6s ease;
          box-shadow: 0 0 10px rgba(96, 165, 250, 0.5);
        }
      `}</style>

      {/* Top Navbar - Fixed */}
      <nav style={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        boxShadow: '0 2px 20px rgba(0,0,0,0.1)',
        padding: '0.75rem 1rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        zIndex: 1000
      }}>
        <Link to="/">
          <img src="/images/p10.jpg" alt="P10 Logo" style={{ height: '40px', borderRadius: '8px' }} />
        </Link>
        <Link to="/support" style={{ 
          color: '#1e3a8a', 
          textDecoration: 'none',
          fontWeight: '600'
        }}>
          Support
        </Link>
      </nav>

      {/* Main Content */}
      <div style={{ flex: 1, padding: '2rem' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.75rem',
            marginBottom: '1rem',
            padding: '0.5rem 1.25rem',
            background: 'rgba(59, 130, 246, 0.2)',
            borderRadius: '100px',
            border: '1px solid rgba(59, 130, 246, 0.3)'
          }}>
            <BarChart3 size={20} />
            <span style={{ fontSize: '0.875rem', fontWeight: '600', letterSpacing: '0.05em' }}>
              AI-POWERED ANALYSIS
            </span>
          </div>
          
          <h1 style={{
            fontSize: '3.5rem',
            fontWeight: '700',
            marginBottom: '1rem',
            background: 'linear-gradient(135deg, #ffffff 0%, #a0a0ff 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            lineHeight: '1.2'
          }}>
            Review Aggregator
          </h1>
          
          <p style={{
            fontSize: '1.25rem',
            color: 'rgba(255, 255, 255, 0.7)',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            Enter any product, service, or business to get a consolidated rating from reviews across the web
          </p>
        </div>

        {/* Search Form */}
        <form onSubmit={handleSubmit} style={{
          marginBottom: '3rem',
          display: 'flex',
          gap: '1rem',
          flexWrap: 'wrap'
        }}>
          <div style={{ flex: '1', minWidth: '300px' }}>
            <input
              type="text"
              className="search-input"
              placeholder="e.g., iPhone 15 Pro, Tesla Model 3, Airbnb..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              disabled={loading}
            />
          </div>
          <button
            type="submit"
            className="search-button"
            disabled={loading || !query.trim()}
          >
            {loading ? (
              <>
                <Loader2 size={20} style={{ animation: 'pulse 1.5s ease-in-out infinite' }} />
                <span style={{ marginLeft: '0.5rem' }}>Analyzing...</span>
              </>
            ) : (
              <>
                <Search size={20} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '0.5rem' }} />
                Analyze Reviews
              </>
            )}
          </button>
        </form>

        {/* Results */}
        {loading && (
          <div style={{
            textAlign: 'center',
            padding: '4rem 2rem',
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '24px',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <Loader2 size={48} style={{ 
              animation: 'pulse 1.5s ease-in-out infinite',
              color: '#3b82f6',
              marginBottom: '1rem'
            }} />
            <p style={{ fontSize: '1.125rem', color: 'rgba(255, 255, 255, 0.7)' }}>
              Searching the web for reviews...
            </p>
          </div>
        )}

        {result && !result.error && (
          <div className="animate-up" style={{
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(20px)',
            borderRadius: '24px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            padding: '3rem',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
          }}>
            {/* Overall Rating Section */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '3rem',
              marginBottom: '3rem',
              flexWrap: 'wrap',
              justifyContent: 'center'
            }}>
              <div className="rating-circle">
                <div className="rating-value">
                  <span className="rating-label">P10 Rating</span>
                  <span className="rating-number">p{result.overallRating}</span>
                </div>
              </div>
              
              <div style={{ flex: '1', minWidth: '300px' }}>
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{
                    fontSize: '0.875rem',
                    color: 'rgba(255, 255, 255, 0.6)',
                    marginBottom: '0.5rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    fontWeight: '600'
                  }}>
                    Overall Rating
                  </div>
                  <div style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '0.75rem' }}>
                    {result.overallRating >= 8 ? 'Excellent' : 
                     result.overallRating >= 6 ? 'Good' : 
                     result.overallRating >= 4 ? 'Fair' : 'Poor'}
                  </div>
                </div>
                
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
                  <div className="pill">
                    <TrendingUp size={16} />
                    {result.confidence} confidence
                  </div>
                  <div className="pill">
                    <Star size={16} />
                    {result.totalReviews} reviews analyzed
                  </div>
                </div>
                
                <p style={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  lineHeight: '1.6',
                  fontSize: '1rem'
                }}>
                  {result.summary}
                </p>
              </div>
            </div>

            {/* Breakdown Stats */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '1.5rem',
              marginBottom: '3rem'
            }}>
              {Object.entries(result.breakdown).map(([key, value]) => (
                <div key={key} className="stat-card">
                  <div style={{
                    fontSize: '0.875rem',
                    color: 'rgba(255, 255, 255, 0.9)',
                    marginBottom: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    fontWeight: '600'
                  }}>
                    {key}
                  </div>
                  <div style={{
                    fontSize: '2rem',
                    fontWeight: '700',
                    fontFamily: 'Space Mono, monospace',
                    marginBottom: '0.75rem',
                    color: '#ffffff'
                  }}>
                    {value}/10
                  </div>
                  <div className="progress-bar">
                    <div className="progress-fill" style={{ width: `${value * 10}%` }} />
                  </div>
                </div>
              ))}
            </div>

            {/* Source Ratings - Show where ratings came from */}
            {result.sourceRatings && result.sourceRatings.length > 0 && (
              <div style={{ marginBottom: '3rem' }}>
                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: '600',
                  marginBottom: '1.5rem',
                  color: '#ffffff'
                }}>
                  Individual Source Ratings
                </h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                  gap: '1rem'
                }}>
                  {result.sourceRatings.map((source, idx) => (
                    <div key={idx} className="stat-card" style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '1.25rem'
                    }}>
                      <div>
                        <div style={{
                          fontSize: '1.1rem',
                          fontWeight: '700',
                          color: '#ffffff',
                          marginBottom: '0.25rem'
                        }}>
                          {source.platform}
                        </div>
                        <div style={{
                          fontSize: '0.875rem',
                          color: 'rgba(255, 255, 255, 0.8)'
                        }}>
                          {source.reviewCount} reviews
                        </div>
                      </div>
                      <div style={{
                        fontSize: '2rem',
                        fontWeight: '700',
                        fontFamily: 'Space Mono, monospace',
                        color: '#ffffff'
                      }}>
                        {source.rating}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pros and Cons */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '2rem'
            }}>
              <div>
                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: '700',
                  marginBottom: '1rem',
                  color: '#10b981',
                  textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}>
                  ✓ Strengths
                </h3>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {result.pros.map((pro, idx) => (
                    <li key={idx} style={{
                      padding: '0.75rem 0',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                      color: '#ffffff',
                      lineHeight: '1.6',
                      fontSize: '1rem'
                    }}>
                      {pro}
                    </li>
                  ))}
                </ul>
              </div>
              
              <div>
                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: '700',
                  marginBottom: '1rem',
                  color: '#ff6b9d',
                  textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}>
                  ✗ Weaknesses
                </h3>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {result.cons.map((con, idx) => (
                    <li key={idx} style={{
                      padding: '0.75rem 0',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                      color: '#ffffff',
                      lineHeight: '1.6',
                      fontSize: '1rem'
                    }}>
                      {con}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {result && result.error && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '16px',
            padding: '2rem',
            textAlign: 'center'
          }}>
            <p style={{ color: '#ef4444', fontSize: '1.125rem' }}>
              {result.message}
            </p>
          </div>
        )}
      </div>
      </div>

      {/* Bottom Navbar - Fixed */}
      <nav style={{ 
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        boxShadow: '0 -2px 20px rgba(0,0,0,0.1)',
        padding: '1rem',
        zIndex: 1000
      }}>
        <div style={{ 
          width: '100%', 
          display: 'flex', 
          justifyContent: 'center',
          flexWrap: 'wrap',
          gap: '0.5rem'
        }}>
          <span style={{ margin: '0 0.5rem', color: '#4a5568', fontWeight: '500' }}>
            p10rating © 2026
          </span>
          <Link to='/privacypolicy' style={{ margin: '0 0.5rem', color: '#3b82f6', textDecoration: 'none', fontWeight: '500' }}>
            Privacy
          </Link>
          <Link to='/disclaimer' style={{ margin: '0 0.5rem', color: '#3b82f6', textDecoration: 'none', fontWeight: '500' }}>
            Disclaimer
          </Link>
          <Link to='/terms' style={{ margin: '0 0.5rem', color: '#3b82f6', textDecoration: 'none', fontWeight: '500' }}>
            Terms
          </Link>
          <Link to='/about' style={{ margin: '0 0.5rem', color: '#3b82f6', textDecoration: 'none', fontWeight: '500' }}>
            About
          </Link>
          <Link to='/support' style={{ margin: '0 0.5rem', color: '#3b82f6', textDecoration: 'none', fontWeight: '500' }}>
            Contact
          </Link>
        </div>
      </nav>
    </div>
  );
}